// HÓNAP-NAVIGÁTOR a partner havi áttekintéséhez: előző / következő + egy hónap-sáv (év-áttekintő), amely a
// VÁLASZ tartományát mutatja (idei január → mostani hónap + 3), és a hónap ALAPJÁT (tény · terv · vegyes) egy
// jellel jelzi. A hónap-választó maga a sáv: egy kattintás = egy hónap. A `MonthStepper` itt nem jó: az
// korlátlanul lép, ez a szerver tartományához kötött.
import type { PortalBillingMonth } from '@atlas/contracts';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { formatMonthAxisLabel, formatMonthLabel } from '@/lib/month';
import { cn } from '@/lib/utils';

const BASIS_DOT: Record<PortalBillingMonth['basis'], string> = {
  actual: 'bg-success',
  plan: 'bg-info',
  mixed: 'bg-warning',
};

export function PortalMonthNav({
  months,
  selected,
  current,
  onSelect,
}: {
  months: readonly PortalBillingMonth[];
  selected: string;
  /** A mostani hónap (`YYYY-MM`) — a sávban kiemelve. */
  current: string;
  onSelect: (month: string) => void;
}): JSX.Element {
  const { t, i18n } = useTranslation();
  const index = months.findIndex((m) => m.month === selected);
  const prev = index > 0 ? months[index - 1] : undefined;
  const next = index >= 0 ? months[index + 1] : undefined;
  const activeRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    // jsdom-ban nincs `scrollIntoView` — a hívás opcionális
    const el = activeRef.current;
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ block: 'nearest', inline: 'center' });
    }
  }, [selected]);

  const arrow =
    'border-border text-muted-foreground hover:text-foreground flex size-8 shrink-0 items-center justify-center rounded-lg border disabled:pointer-events-none disabled:opacity-40';

  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        className={arrow}
        aria-label={t('common.prevMonth')}
        disabled={prev === undefined}
        onClick={() => prev && onSelect(prev.month)}
      >
        <ChevronLeftIcon className="size-4" />
      </button>
      <div
        role="group"
        aria-label={t('portal.billing.month.picker')}
        className="flex min-w-0 flex-1 gap-1 overflow-x-auto py-0.5"
      >
        {months.map((m) => {
          const active = m.month === selected;
          return (
            <button
              key={m.month}
              ref={active ? activeRef : undefined}
              type="button"
              aria-pressed={active}
              aria-label={formatMonthLabel(m.month, i18n.language)}
              data-basis={m.basis}
              onClick={() => onSelect(m.month)}
              className={cn(
                'flex h-9 min-w-12 shrink-0 flex-col items-center justify-center gap-0.5 rounded-lg px-2 text-xs font-medium transition-colors duration-150',
                active
                  ? 'bg-brand-soft text-brand-dark dark:text-brand'
                  : 'text-muted-foreground hover:bg-sunken',
                m.month === current && !active && 'ring-border ring-1',
              )}
            >
              <span>{formatMonthAxisLabel(m.month, i18n.language)}</span>
              <span
                aria-hidden="true"
                className={cn('size-1.5 rounded-full', BASIS_DOT[m.basis])}
              />
            </button>
          );
        })}
      </div>
      <button
        type="button"
        className={arrow}
        aria-label={t('common.nextMonth')}
        disabled={next === undefined}
        onClick={() => next && onSelect(next.month)}
      >
        <ChevronRightIcon className="size-4" />
      </button>
    </div>
  );
}
